/**
 * Single source of truth for LMS sidebar navigation and module visibility.
 * Role Management derives module checkboxes from getRegisteredLmsModules().
 * Do not maintain a separate module list elsewhere.
 */
import {
  LayoutDashboard, Package, FileText, Coins, ArrowDownUp,
  FolderTree, FileType, Shield, Key, Link2,
  AlertTriangle, Receipt, Calculator, Percent, Clock, CreditCard, Gift, Users,
  ArrowLeftRight, RefreshCw, Trash2, Undo2, Database, Play, Unlock, Banknote,
  TrendingUp, Wrench, RotateCcw, ListOrdered, Building2, Mail,
  Settings, FileSignature, Bell, Scale, XCircle, MessageSquare, Snowflake,
} from 'lucide-react';

/** Core lending sidebar (tenant users). Each section/item may carry `module`. */
export const LMS_LENDING_NAV = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { type: 'separator', label: 'Loan Management', module: 'loan_management' },
  { name: 'Loans', href: '/loans', icon: FileText, module: 'loan_management' },
  { name: 'Agreements', href: '/agreements', icon: FileSignature, module: 'loan_management' },
  { name: 'Disbursements', href: '/disbursements', icon: Coins, module: 'loan_management' },
  { name: 'Repayments', href: '/repayments', icon: ArrowDownUp, module: 'loan_management' },
  { type: 'separator', label: 'Platform Configuration' },
  { name: 'Loan Products', href: '/loan-products', icon: FolderTree, module: 'loan_management' },
  { name: 'Loan Types', href: '/loan-types', icon: Package, module: 'loan_management' },
  { name: 'Document Types', href: '/document-types', icon: FileType, module: 'loan_management' },

  { name: 'Agreement Configuration', href: '/platform-config/agreements', icon: FileText, requiresTenantAdmin: true },
  { name: 'Charge Configuration', href: '/platform-config/charges', icon: Receipt, requiresTenantAdmin: true },
  { name: 'Interest Rate Configuration', href: '/platform-config/interest-rates', icon: Percent, requiresTenantAdmin: true },
  { name: 'Risk & Classification', href: '/risk', icon: Shield, module: 'system' },
  {
    type: 'group',
    id: 'platform-configuration-notifications',
    name: 'Notifications',
    icon: Bell,
    requiresTenantAdmin: true,
    children: [
      { name: 'Email', href: '/platform-config/notifications/email', icon: Mail, requiresTenantAdmin: true },
      { name: 'SMS', href: '/platform-config/notifications/sms', icon: MessageSquare, requiresTenantAdmin: true },
    ],
  },
  { type: 'separator', label: 'Security & Collateral', module: 'security_collateral' },
  { name: 'Security Types', href: '/security/types', icon: Key, module: 'security_collateral' },
  { name: 'Securities', href: '/security/securities', icon: Shield, module: 'security_collateral' },
  { name: 'Assignments', href: '/security/assignments', icon: Link2, module: 'security_collateral' },
  { name: 'Releases', href: '/security/releases', icon: Unlock, module: 'security_collateral' },
  { name: 'Deposits', href: '/security/deposits', icon: Banknote, module: 'security_collateral' },
  { name: 'Prices', href: '/security/prices', icon: TrendingUp, module: 'security_collateral' },
  { name: 'Price History', href: '/security/prices/history', icon: Clock, module: 'security_collateral' },
  { name: 'Shortfalls', href: '/security/shortfalls', icon: AlertTriangle, module: 'security_collateral' },
  { type: 'separator', label: 'Charges & Interest', module: 'charges_interest' },
  { name: 'Interest Accruals', href: '/charges/accruals', icon: Percent, module: 'charges_interest' },
  { type: 'separator', label: 'Demands & Collections', module: 'demands_collections' },
  { name: 'Loan Demands', href: '/demands', icon: FileText, module: 'demands_collections' },
  { name: 'Overdue Loans', href: '/demands/overdue', icon: Clock, module: 'demands_collections' },
  { name: 'Payments', href: '/demands/payments', icon: CreditCard, module: 'demands_collections' },
  { name: 'Waivers', href: '/demands/waivers', icon: Gift, module: 'demands_collections' },
  { type: 'separator', label: 'Co-Lending & Transfers', module: 'co_lending_transfers' },
  { name: 'Loan Partners', href: '/partners', icon: Users, module: 'co_lending_transfers' },
  { name: 'Loan Transfers', href: '/transfers', icon: ArrowLeftRight, module: 'co_lending_transfers' },
  { name: 'Co-Lending Mgmt', href: '/co-lending', icon: Link2, module: 'co_lending_transfers' },
  { type: 'separator', label: 'Loan Operations', module: 'loan_operations' },
  { name: 'Restructures', href: '/restructures', icon: RefreshCw, module: 'loan_operations' },
  { name: 'Write-Offs', href: '/write-offs', icon: Trash2, module: 'loan_operations' },
  { name: 'Refunds', href: '/refunds', icon: Undo2, module: 'loan_operations' },
  { name: 'Loan Cancellations', href: '/compliance/cancellations', icon: XCircle, module: 'compliance' },
  { name: 'Loan Freeze', href: '/loan-freeze', icon: Snowflake, module: 'loan_operations' },
  { name: 'Adjustments', href: '/charges/adjustments', icon: Calculator, module: 'charges_interest' },
  { type: 'separator', label: 'System', module: 'system' },
  { name: 'Reports', href: '/reports', icon: TrendingUp, module: 'reports', moduleLabel: 'Reports' },
  { name: 'Accounting', href: '/accounting', icon: Database, module: 'system' },
  { name: 'Process Jobs', href: '/process-jobs', icon: Play, module: 'system' },
  { type: 'separator', label: 'Tools', module: 'tools' },
  { name: 'Offset Order', href: '/tools/offset-order', icon: ListOrdered, module: 'tools' },
  { name: 'Bulk Repayment', href: '/tools/bulk-repayment', icon: CreditCard, module: 'tools' },
  { name: 'Repost Tools', href: '/tools/repost', icon: RotateCcw, module: 'tools' },
  { name: 'Admin & Import', href: '/admin-tools', icon: Wrench, module: 'tools' },
];

export const LMS_COMPLIANCE_NAV = [
  { type: 'separator', label: 'Compliance', module: 'compliance' },
  { name: 'CIBIL Reporting', href: '/compliance/cibil-reporting', icon: Scale, module: 'compliance' },
];

export const LMS_SETTINGS_NAV = [
  { type: 'separator', label: 'Settings', module: 'settings' },
  { name: 'Integrations', href: '/settings/integrations', icon: Settings, module: 'settings' },
];

export const LMS_USER_MANAGEMENT_GROUP = {
  type: 'group',
  name: 'User Management',
  icon: Users,
  module: 'user_management',
  children: [
    { name: 'Users Directory', href: '/user-management/users', icon: Users, module: 'user_management' },
    { name: 'Role Management', href: '/user-management/roles', icon: Shield, module: 'user_management' },
    { name: 'Branches', href: '/user-management/branches', icon: Building2, module: 'user_management' },
    {
      name: 'MFA Settings',
      href: '/user-management/mfa',
      icon: Key,
      module: 'user_management',
      requiresTenantAdmin: true,
    },
  ],
};

export const LMS_TENANT_ADMIN_NAV = [
  { type: 'separator', label: 'Company Admin', module: 'company_admin' },
  LMS_USER_MANAGEMENT_GROUP,
  { name: 'Credit Balance', href: '/tenant/credits', icon: CreditCard, module: 'company_admin' },
];

/** Full tenant LMS nav (order matches sidebar). */
export function getAllLmsNavItems() {
  return [
    ...LMS_LENDING_NAV,
    ...LMS_COMPLIANCE_NAV,
    ...LMS_SETTINGS_NAV,
    ...LMS_TENANT_ADMIN_NAV,
  ];
}

/**
 * Unique LMS modules registered in the sidebar — used by Role Management.
 * Adding a separator/group/item with a new `module` key automatically registers it.
 */
export function getRegisteredLmsModules() {
  const map = new Map();

  const registerItem = (item) => {
    if (!item?.module || map.has(item.module)) return;
    if (item.type === 'separator') {
      map.set(item.module, item.label);
    } else if (item.type === 'group') {
      map.set(item.module, item.name);
    } else {
      map.set(item.module, item.moduleLabel || item.name || item.label || item.module);
    }
  };

  const visitForPriority = (item) => {
    registerItem(item);
    (item.children || []).forEach(visitForPriority);
  };

  const visit = (item) => {
    registerItem(item);
    (item.children || []).forEach(visit);
  };

  // Prefer separator/group labels: walk twice — first separators/groups, then items
  getAllLmsNavItems().forEach((item) => {
    if (item.type === 'separator' || item.type === 'group') visitForPriority(item);
  });
  getAllLmsNavItems().forEach(visit);

  return Array.from(map.entries()).map(([resource, label]) => ({ resource, label }));
}

export function formatRoleLabel(name) {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Flatten registry entries that have an href (including group children). */
export function getLmsNavLinkItems() {
  const links = [];
  const visit = (item) => {
    if (item?.href) links.push(item);
    (item.children || []).forEach(visit);
  };
  getAllLmsNavItems().forEach(visit);
  return links;
}

/**
 * Resolve the LMS module key for a pathname from the sidebar registry.
 * Longer href prefixes win (e.g. /security/prices/history over /security/prices).
 */
export function getModuleForPath(pathname) {
  if (!pathname) return null;
  let best = null;
  let bestLen = -1;
  for (const item of getLmsNavLinkItems()) {
    const href = item.href;
    if (!href) continue;
    const match =
      pathname === href ||
      (href !== '/' && pathname.startsWith(`${href}/`));
    if (match && href.length > bestLen) {
      best = item;
      bestLen = href.length;
    }
  }
  return best?.module || null;
}

/** Whether the nav item requires tenant-admin (MFA Settings). */
export function navItemRequiresTenantAdmin(item) {
  return !!item?.requiresTenantAdmin;
}

/**
 * Module visibility check used by sidebar + route guards.
 * tenant_admin always passes. Missing/non-array module_access denies access
 * (do not fall back to showing all modules — that breaks RBAC for stale sessions).
 */
export function userHasModuleAccess(user, moduleKey) {
  if (!moduleKey) return true;
  if (!user) return false;
  if (user.is_tenant_admin || (user.roles || []).includes('tenant_admin')) return true;
  const access = user.module_access;
  if (!Array.isArray(access)) return false;
  return access.includes(moduleKey);
}

export function userIsTenantAdmin(user) {
  if (!user) return false;
  return !!(user.is_tenant_admin || (user.roles || []).includes('tenant_admin'));
}

export function navItemVisible(user, item) {
  if (navItemRequiresTenantAdmin(item) && !userIsTenantAdmin(user)) return false;
  // risk_manager always sees the risk and loan-freeze pages
  if ((item?.href === '/risk' || item?.href === '/loan-freeze') && (user?.roles || []).includes('risk_manager')) return true;
  return userHasModuleAccess(user, item?.module);
}

/** Remove separators with no visible items following them (before the next separator). */
function removeOrphanSeparators(items) {
  const cleaned = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.type === 'separator') {
      const rest = items.slice(i + 1);
      if (!rest.some((n) => n.type !== 'separator')) continue;
      const nextSep = rest.findIndex((n) => n.type === 'separator');
      const section = nextSep === -1 ? rest : rest.slice(0, nextSep);
      if (section.length === 0) continue;
    }
    cleaned.push(item);
  }
  return cleaned;
}

export function filterNavByModuleAccess(items, user) {
  const visible = [];
  for (const item of items) {
    if (item.type === 'separator') {
      // Keep separators for now; empty sections are removed in the cleanup pass.
      // This lets mixed-module sections (e.g. Loan Operations) still show a heading
      // when any child item is visible.
      visible.push(item);
      continue;
    }
    if (item.type === 'group') {
      if (navItemRequiresTenantAdmin(item) && !userIsTenantAdmin(user)) continue;
      // Visibility is child-driven so mixed-module groups still show only allowed screens.
      const children = (item.children || []).filter((c) => navItemVisible(user, c));
      if (children.length === 0) continue;
      visible.push({ ...item, children });
      continue;
    }
    if (!navItemVisible(user, item)) continue;
    visible.push(item);
  }
  // Drop separators that have no following visible entries before the next separator
  return removeOrphanSeparators(visible);
}
